import type { MeetingNotes, MeetingTranscript } from '@live-stream/shared'
import type { Meetings } from '../api/meetings.js'
import type { Rooms } from '../api/rooms.js'
import { ApiError } from '../http.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { playable, turns, type Turn } from './transcript-view.js'

/**
 * One meeting's page (Phase 10): its transcript, the recording beside it,
 * and Claude's notes, kept up to date while Claude writes them. The player
 * and the scrolling stay the page's; the times it needs are in
 * transcript-view (`lineAt`, `recordingSecondsAt`, `meetingMsAt`).
 *
 * `open(sessionId)` when the page opens, `close()` when it goes.
 */

export interface MeetingPageState {
  transcript: MeetingTranscript | null
  loading: boolean
  error: string | null
  /** A signed link to play the meeting's recording, when it has one. */
  videoUrl: string | null
}

type Recording = MeetingTranscript['recordings'][number]

const NOTES_POLL_MS = 4000

export class MeetingPage {
  private readonly store = writable<MeetingPageState>({
    transcript: null,
    loading: true,
    error: null,
    videoUrl: null,
  })
  readonly state: ReadableStore<MeetingPageState> = readonly(this.store)
  /** The recording the transcript plays against, when there is one. */
  readonly recording: ReadableStore<Recording | null>
  /** What was said, in turns: one speaker's lines until someone else speaks or a pause. */
  readonly turns: ReadableStore<Turn[]>
  /** Everyone who spoke, in the order they first did. */
  readonly speakers: ReadableStore<Array<{ key: string; name: string }>>

  private sessionId: string | null = null
  private notesPoll: ReturnType<typeof setInterval> | null = null
  private unwatchNotes: (() => void) | null = null

  constructor(
    private readonly meetings: Meetings,
    private readonly rooms: Rooms,
    private readonly signedIn: ReadableStore<boolean>,
  ) {
    const transcript = derived([this.store], (s) => s.transcript)
    this.recording = derived([transcript], (t) => (t ? playable(t.recordings) : null))
    this.turns = derived([transcript], (t) => (t ? turns(t.segments) : []))
    this.speakers = derived([transcript], (t) => {
      const seen = new Map<string, string>()
      for (const s of t?.segments ?? []) {
        if (!seen.has(s.speakerKey)) seen.set(s.speakerKey, s.speakerName)
      }
      return [...seen.entries()].map(([key, name]) => ({ key, name }))
    })
  }

  /** Load the meeting (members only), and follow its notes until `close()`. */
  async open(sessionId: string): Promise<void> {
    this.close()
    this.sessionId = sessionId
    this.store.set({ transcript: null, loading: true, error: null, videoUrl: null })
    // While Claude writes the notes, look again every few seconds.
    this.unwatchNotes = watch(
      this.store,
      (s) => s.transcript?.notes.status,
      (status) => {
        const writing = status === 'pending' || status === 'processing'
        if (writing && !this.notesPoll) {
          this.notesPoll = setInterval(() => void this.refreshNotes(), NOTES_POLL_MS)
        } else if (!writing && this.notesPoll) {
          clearInterval(this.notesPoll)
          this.notesPoll = null
        }
      },
      { immediate: true },
    )
    if (!this.signedIn.get()) {
      patch(this.store, { loading: false })
      return
    }
    try {
      const transcript = await this.meetings.transcript(sessionId)
      patch(this.store, { transcript, error: null })
      const r = playable(transcript.recordings)
      if (r) patch(this.store, { videoUrl: (await this.rooms.recording(r.id)).links.video })
    } catch (err) {
      patch(this.store, {
        error:
          err instanceof ApiError && err.status === 404
            ? 'This meeting has no transcript.'
            : err instanceof Error
              ? err.message
              : 'Could not load the transcript.',
      })
    } finally {
      patch(this.store, { loading: false })
    }
  }

  close(): void {
    this.unwatchNotes?.()
    this.unwatchNotes = null
    if (this.notesPoll) clearInterval(this.notesPoll)
    this.notesPoll = null
  }

  private async refreshNotes(): Promise<void> {
    if (!this.sessionId) return
    try {
      const t = await this.meetings.transcript(this.sessionId)
      this.adoptNotes(t.notes)
    } catch {
      // Next time.
    }
  }

  /** Newer notes: Claude's, or someone's edit. */
  adoptNotes(notes: MeetingNotes): void {
    const transcript = this.store.get().transcript
    if (transcript) patch(this.store, { transcript: { ...transcript, notes } })
  }

  /** Hosts: delete the transcript, and the notes with it; true once gone. */
  async remove(): Promise<boolean> {
    if (!this.sessionId) return false
    try {
      await this.meetings.deleteTranscript(this.sessionId)
      return true
    } catch (err) {
      patch(this.store, { error: err instanceof Error ? err.message : 'Could not delete it.' })
      return false
    }
  }
}
